import { computed, ref, watch } from 'vue';
import api from '@/api/index';
import { PageName, SummaryInfo } from '@/api/tracking';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { FeedSummaryFeature } from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useLayoutVariant } from '@/utils/composables/useLayoutVariant';

type FeedbackPolarity = 'positive' | 'negative';

type FeedbackOption = {
  id: string;
  label: string;
  value: boolean;
};

const POSITIVE_FEEDBACK_OPTIONS: readonly Omit<FeedbackOption, 'value'>[] = [
  { id: 'matches_interest', label: '自分が知りたい視点に合っていた' },
  { id: 'trustworthy', label: '正確で、事実として信頼できた' },
  { id: 'appropriate_volume', label: '情報量がちょうどよかった' },
  { id: 'easy_to_read', label: 'わかりやすく、読みやすい' },
];

const NEGATIVE_FEEDBACK_OPTIONS: readonly Omit<FeedbackOption, 'value'>[] = [
  { id: 'off_target', label: '自分が知りたい視点に合っていなかった' },
  { id: 'inaccurate', label: '誤りがある、または正確でないと感じた' },
  { id: 'too_sparse', label: '情報が少なすぎる' },
  { id: 'too_verbose', label: '情報が多すぎる' },
  { id: 'hard_to_read', label: 'わかりにくく、読みにくい' },
];

const createOptions = (
  options: readonly Omit<FeedbackOption, 'value'>[],
): FeedbackOption[] => options.map(option => ({ ...option, value: false }));

export const useFeedSummaryFeedback = (props: {
  summaryInfo?: SummaryInfo;
  feature: FeedSummaryFeature;
  pageName: PageName;
}) => {
  const { getCurrentPathForTracking } = useCurrentPathForTracking();
  const { createSnackbar } = useSnackbar();
  const layoutVariant = useLayoutVariant();

  const isDialogOpen = ref(false);
  const dialogStatus = ref<FeedbackPolarity | null>(null);
  const isSubmitting = ref(false);

  const positiveFeedbacks = ref(createOptions(POSITIVE_FEEDBACK_OPTIONS));
  const negativeFeedbacks = ref(createOptions(NEGATIVE_FEEDBACK_OPTIONS));

  const canSubmit = computed(() => {
    if (isSubmitting.value) return false;

    if (dialogStatus.value === 'positive') {
      return positiveFeedbacks.value.some(option => option.value);
    } else if (dialogStatus.value === 'negative') {
      return negativeFeedbacks.value.some(option => option.value);
    }

    return false;
  });

  const openDialog = (status: FeedbackPolarity) => {
    dialogStatus.value = status;
    isDialogOpen.value = true;
  };

  watch(isDialogOpen, open => {
    if (!open) {
      dialogStatus.value = null;
      positiveFeedbacks.value.forEach(option => (option.value = false));
      negativeFeedbacks.value.forEach(option => (option.value = false));
    }
  });

  const showSuccess = (status: FeedbackPolarity) => {
    createSnackbar({
      message:
        status === 'positive'
          ? 'この回答を気に入っていただき、ありがとうございます'
          : 'フィードバックをいただきありがとうございます',
      type: 'success',
    });
  };

  const submitFeedback = async () => {
    if (dialogStatus.value === null) return;

    const checkedOptions =
      dialogStatus.value === 'positive'
        ? positiveFeedbacks.value.filter(option => option.value)
        : negativeFeedbacks.value.filter(option => option.value);

    const feedbackReasons = checkedOptions.map(option => option.id);
    const feedbackLabels = checkedOptions.map(option => option.label);

    isSubmitting.value = true;
    try {
      await api.trackEvent(
        'summary_feedback_submitted',
        {
          feature: props.feature,
          pageUrl: getCurrentPathForTracking(),
          pageName: props.pageName,
          layoutVariant,
        },
        undefined,
        undefined,
        {
          feedback: {
            polarity: dialogStatus.value,
            reasons: feedbackReasons,
            labels: feedbackLabels,
          },
          ...props.summaryInfo,
        },
      );

      showSuccess(dialogStatus.value);
    } catch {
      createSnackbar({
        message: 'フィードバックの送信に失敗しました',
        type: 'error',
      });
    } finally {
      isSubmitting.value = false;
      isDialogOpen.value = false;
    }
  };

  return {
    isDialogOpen,
    dialogStatus,
    positiveFeedbacks,
    negativeFeedbacks,
    canSubmit,
    openDialog,
    submitFeedback,
  };
};
